import type { Card, CardCivilization, CivFilterToken, CivMatchMode, CardType } from "./index";
import { type DeckFormat, FORMAT_CUTOFF } from "./formats";


// 一部の生データ（DM-18/19 など）は名前にルビ（読み）が併記されている:
// 例 "青銅の鎧(ブロンズ・アーム・トライブ)"。表示・重複判定ともにルビは落として素の名前で扱う。
// カタカナと中黒だけで構成された末尾の括弧のみをルビとみなす（他の括弧付き名を壊さないため）。
const RUBY_SUFFIX = /[(（][ァ-ヴー・\s]+[)）]\s*$/;

export function stripRuby(name: string): string {
  const stripped = name.replace(RUBY_SUFFIX, "").trim();
  return stripped || name;
}

// 画像のURL基点。スナップショットの imageUrl は "/images/cardimage/<file>" の相対パスで保持し、
// initCards が基点を前置する（カード画像は Cloudflare R2 → Worker 経由で配信）。
export const DEFAULT_IMAGE_BASE = "https://dm-online-worker.yunosuke0802.workers.dev";

// DB（D1）に保存し、Worker/frontend が起動時に取り込むカードデータ一式。
export interface CardSnapshot {
  // 識別子。データを更新するたびに変える（キャッシュ無効化用）
  version: string;
  // 正規カード（重複排除済み）
  cards: Card[];
  // 刷りID → 正規カードID（重複排除で落とされた再録の解決用）
  aliases: Record<string, string>;
  // 刷りID → その刷り固有の画像URL
  printImages: Record<string, string>;
  // 弾コードの時系列順。legality はこの index の大小で判定する
  setOrder: string[];
}

interface CardStore {
  cards: Card[];
  index: Map<string, Card>;
  aliases: Map<string, string>;
  printImages: Map<string, string>;
  setOrder: string[];
  setIndexOf: Map<string, number>;
  races: readonly string[];
  version: string;
}

let store: CardStore | null = null;

function requireStore(): CardStore {
  if (!store) throw new Error("カードデータが未初期化です（initCards を先に呼んでください）");
  return store;
}

export function isCardsReady(): boolean {
  return store !== null;
}

export function getCardsVersion(): string | undefined {
  return store?.version;
}

const prefixImage = (base: string, url: string | undefined) => (url ? `${base}${url}` : url);

// スナップショットをモジュール内ストアへ展開する。以降 getCardById 等は同期で引ける。
export function initCards(snapshot: CardSnapshot, imageBase: string = DEFAULT_IMAGE_BASE): void {
  const withBase = (c: Card): Card => ({
    ...c,
    imageUrl: prefixImage(imageBase, c.imageUrl),
    ...(c.faces ? { faces: c.faces.map((f) => ({ ...f, imageUrl: prefixImage(imageBase, f.imageUrl) })) as Card["faces"] } : {}),
    ...(c.arts ? { arts: c.arts.map((a) => ({ ...a, imageUrl: prefixImage(imageBase, a.imageUrl) })) } : {}),
  });
  const cards = snapshot.cards.map(withBase);
  const races = [...new Set(cards.flatMap(racesOf))].sort((a, b) => a.localeCompare(b, "ja"));
  store = {
    cards,
    index: new Map(cards.map((c) => [c.id, c])),
    aliases: new Map(Object.entries(snapshot.aliases)),
    printImages: new Map(Object.entries(snapshot.printImages).map(([id, url]) => [id, prefixImage(imageBase, url)!])),
    setOrder: snapshot.setOrder,
    setIndexOf: new Map(snapshot.setOrder.map((code, i) => [code, i])),
    races,
    version: snapshot.version,
  };
}

export function getAllCards(): Card[] {
  return requireStore().cards;
}

// 弾コードの時系列順リスト。
export function getSetCodes(): string[] {
  return requireStore().setOrder;
}

// カードが指定フォーマットで使用可能か（初出弾が cutoff 以下か）を判定する。
export function isCardLegalInFormat(card: Card, format: DeckFormat): boolean {
  const { setIndexOf } = requireStore();
  const cardIdx = setIndexOf.get(card.set);
  const cutoffIdx = setIndexOf.get(FORMAT_CUTOFF[format]);
  if (cardIdx === undefined || cutoffIdx === undefined) return true;
  return cardIdx <= cutoffIdx;
}

// 再録カードの id を、実際に保持しているカードの id へ解決する。
export function resolveCardId(id: string): string {
  return requireStore().aliases.get(id) ?? id;
}

export function getCardById(id: string): Card | undefined {
  const { index } = requireStore();
  return index.get(id) ?? index.get(resolveCardId(id));
}

// 刷りID から表示すべき画像URLを引く。
// 再録違いの id（例 "dmr13-109"）ならその刷り固有の画像を返し、
// 未知の id は正規カードの既定画像へフォールバックする。
export function getCardImage(id: string): string | undefined {
  return requireStore().printImages.get(id) ?? getCardById(id)?.imageUrl;
}

// 画像URLをサムネイル（幅240px の WebP）の URL に変える。一覧・盤面など小さく表示する箇所で使い、
// 詳細表示やデッキ画像出力は原寸のまま使う。カード画像以外の URL はそのまま返す。
export function toThumbUrl(url: string): string;
export function toThumbUrl(url: string | undefined): string | undefined;
export function toThumbUrl(url: string | undefined): string | undefined {
  return url?.replace("/images/cardimage/", "/images/thumb/");
}

export interface CardFilter {
  civilization?: CardCivilization;
  multiCiv?: boolean;
  // false のとき多色（2文明以上）カードを結果から除外する。未指定/true なら含める
  includeMulti?: boolean;
  // 選択中の文明集合 S（無色を含む）。空/未指定なら文明での絞り込みなし。
  civs?: CivFilterToken[];
  // civs に対する一致モード。未指定なら「S のいずれかを含む」（OR）で絞り込む。
  civMode?: CivMatchMode;
  type?: CardType;
  query?: string;
  cost?: number;
  // 種族名（単一）。複合種族カードはいずれかが一致すれば残す
  race?: string;
  // キーワード能力（ABILITY_KEYWORDS の match）。複数指定時はすべて備えるカードのみ残す（AND）
  keywords?: string[];
  // 指定時、そのフォーマットで使用可能なカードのみを残す
  format?: DeckFormat;
}

// 種族は "アーマード・ドラゴン/サムライ" のように "/" 区切りで複数持つ。
// 両面カード（サイキック/ドラグハート）は裏面の種族も対象にする。
function racesOf(card: Card): string[] {
  const sources = [card.race, ...(card.faces ?? []).map((f) => f.race)];
  const out: string[] = [];
  for (const src of sources) {
    if (!src) continue;
    for (const part of src.split("/")) {
      const race = part.trim();
      if (race && !out.includes(race)) out.push(race);
    }
  }
  return out;
}

// 全カードに登場する種族の一覧（重複なし・五十音順）。絞り込みUIの選択肢に使う
export function getAllRaces(): readonly string[] {
  return requireStore().races;
}

export interface AbilityKeyword {
  // チップに表示する名前
  label: string;
  // カードテキスト照合に使う語（表記ゆれ・ルビは正規化で吸収）
  match: string;
}

// 絞り込みに使うキーワード能力。docs/RULES.md 8章のキーワード能力を網羅し、
// カテゴリごとにまとめる。判定は abilityTextOf（ルビ・「」参照を除去）した
// カードテキストに対する substring 一致（表記ゆれは normalizeForSearch で吸収）。
// 収録弾（DM-01〜DMX-19）に実在するもののみ掲載。
export const ABILITY_KEYWORD_GROUPS: readonly { label: string; keywords: readonly AbilityKeyword[] }[] = [
  {
    label: "攻撃・ブレイク",
    keywords: [
      { label: "スピードアタッカー", match: "スピードアタッカー" },
      { label: "パワーアタッカー", match: "パワーアタッカー" },
      { label: "スレイヤー", match: "スレイヤー" },
      { label: "W・ブレイカー", match: "W・ブレイカー" },
      { label: "T・ブレイカー", match: "T・ブレイカー" },
      { label: "Q・ブレイカー", match: "Q・ブレイカー" },
      { label: "ワールド・ブレイカー", match: "ワールド・ブレイカー" },
      { label: "ステルス", match: "ステルス" },
      { label: "フリーズ", match: "フリーズ" },
      { label: "ターボラッシュ", match: "ターボラッシュ" },
      { label: "ブレイク・ボーナス", match: "ブレイク・ボーナス" },
      { label: "ハンティング", match: "ハンティング" },
    ],
  },
  {
    label: "防御・受け",
    keywords: [
      { label: "ブロッカー", match: "ブロッカー" },
      { label: "ガードマン", match: "ガードマン" },
      { label: "S・トリガー", match: "S・トリガー" },
      { label: "S・トリガーX", match: "S・トリガーX" },
      { label: "ストライクバック", match: "S・バック" },
      { label: "ニンジャ・ストライク", match: "ニンジャ・ストライク" },
      { label: "セイバー", match: "セイバー" },
      { label: "シールド・セイバー", match: "シールド・セイバー" },
      { label: "ウルトラ・セイバー", match: "ウルトラ・セイバー" },
      { label: "シールド・ゴー", match: "シールド・ゴー" },
      { label: "シールド・フォース", match: "シールド・フォース" },
      { label: "ホーリー・フィールド", match: "ホーリー・フィールド" },
    ],
  },
  {
    label: "コスト軽減・踏み倒し",
    keywords: [
      { label: "マナ武装", match: "マナ武装" },
      { label: "シンパシー", match: "シンパシー" },
      { label: "ソウルシフト", match: "ソウルシフト" },
      { label: "G・ゼロ", match: "G・ゼロ" },
      { label: "マナ爆誕", match: "マナ爆誕" },
      { label: "アタック・チャンス", match: "アタック・チャンス" },
      { label: "ジェネレート", match: "ジェネレート" },
      { label: "侍流ジェネレート", match: "侍流ジェネレート" },
      { label: "連鎖", match: "連鎖" },
      { label: "激流連鎖", match: "激流連鎖" },
      { label: "チャージャー", match: "チャージャー" },
      { label: "サイクロン", match: "サイクロン" },
      { label: "スペース・チャージ", match: "スペース・チャージ" },
    ],
  },
  {
    label: "条件付き強化・その他",
    keywords: [
      { label: "メタモーフ", match: "メタモーフ" },
      { label: "ウェーブストライカー", match: "ウェーブストライカー" },
      { label: "サバイバー", match: "サバイバー" },
      { label: "ダイナモ", match: "ダイナモ" },
      { label: "サイレントスキル", match: "サイレントスキル" },
      { label: "ナイト・マジック", match: "ナイト・マジック" },
      { label: "アクセル", match: "アクセル" },
      { label: "マーシャル・タッチ", match: "マーシャル・タッチ" },
      { label: "返霊", match: "返霊" },
      { label: "殲滅返霊", match: "殲滅返霊" },
      { label: "仁義", match: "仁義" },
      { label: "ノー・チョイス", match: "ノー・チョイス" },
      { label: "スリリング・スリー", match: "スリリング・スリー" },
      { label: "L・ゲート", match: "L・ゲート" },
      { label: "ロスト・プリズム", match: "ロスト・プリズム" },
      { label: "メテオバーン", match: "メテオバーン" },
    ],
  },
  {
    label: "ゴッド・超次元・ドラグハート",
    keywords: [
      { label: "G・リンク", match: "G・リンク" },
      { label: "エターナル・Ω", match: "エターナル・Ω" },
      { label: "覚醒", match: "覚醒" },
      { label: "覚醒リンク", match: "覚醒リンク" },
      { label: "リンク解除", match: "リンク解除" },
      { label: "解除", match: "解除" },
      { label: "ドロン・ゴー", match: "ドロン・ゴー" },
      { label: "龍解", match: "龍解" },
    ],
  },
];

// グループを平坦化した全キーワード（フィルタUI・テストの列挙に使う）
export const ABILITY_KEYWORDS: readonly AbilityKeyword[] =
  ABILITY_KEYWORD_GROUPS.flatMap((g) => g.keywords);

// キーワード能力の判定に使うテキスト。両面カードは裏面のテキストも対象にする。
// カッコ（ルビ・能力の説明文）と「」『』（能力を除去/付与する参照）を除いて、
// カード自身が持つ能力の見出しだけを残す。これで:
//  - "W(ダブル)・ブレイカー" のようなルビ入り表記も "W・ブレイカー" として拾える
//  - "「ブロッカー」を持つクリーチャーを破壊する" のような参照は誤ヒットさせない
function abilityTextOf(card: Card): string {
  return [card.text, ...(card.faces ?? []).map((f) => f.text)]
    .filter(Boolean)
    .join("\n")
    .replace(/[「『][^」』]*[」』]/g, "")
    .replace(/[（(][^（）()]*[）)]/g, "");
}

// 検索用の正規化: 全角/半角の揺れを吸収し、中黒・スペースを無視して比較する。
// IME の変換ミスで「ガルべリアス」のようにひらがなが混ざることがあるため、
// ひらがなはカタカナに寄せて同一視する（半角カナは NFKC で全角カタカナになる）。
function toKatakana(s: string): string {
  return s.replace(/[\u3041-\u3096]/g, (c) => String.fromCharCode(c.charCodeAt(0) + 0x60));
}

function normalizeForSearch(s: string): string {
  return toKatakana(s.normalize("NFKC").toLowerCase()).replace(/[・\s]/g, "");
}

// カードの文明トークン集合。無色（civilization が空）は "colorless" 1要素として扱う。
function civTokensOf(card: Card): CivFilterToken[] {
  return card.civilization.length === 0 ? ["colorless"] : card.civilization;
}

// カードごとの検索用正規化テキスト。入力のたびに全カード分を作り直さないよう初回だけ計算して保持する。
interface SearchText { name: string; text: string; ability: string }
const searchTextCache = new WeakMap<Card, SearchText>();
function searchTextOf(card: Card): SearchText {
  let v = searchTextCache.get(card);
  if (!v) {
    v = {
      name: normalizeForSearch(card.name),
      text: normalizeForSearch(card.text),
      ability: normalizeForSearch(abilityTextOf(card)),
    };
    searchTextCache.set(card, v);
  }
  return v;
}

export function filterCards(filter: CardFilter): Card[] {
  // クエリ・キーワードは全カード共通なので、ループの外で1回だけ正規化する
  const nq = filter.query ? normalizeForSearch(filter.query) : "";
  const nKeywords = filter.keywords?.map(normalizeForSearch) ?? [];
  return getAllCards().filter((card) => {
    if (filter.format && !isCardLegalInFormat(card, filter.format)) return false;
    if (filter.multiCiv && card.civilization.length < 2) return false;
    if (filter.includeMulti === false && card.civilization.length >= 2) return false;
    if (filter.civilization && !card.civilization.includes(filter.civilization)) return false;
    if (filter.civs || filter.civMode) {
      const S = new Set<CivFilterToken>(filter.civs ?? []);
      // 無色は「色」ではないため、単色/多色（色数）の判定からは除外する。
      const colors = card.civilization; // 実際の色（無色カードは空配列）
      const colorTokensInS = S.size - (S.has("colorless") ? 1 : 0);
      switch (filter.civMode) {
        case "single": // S に属する単色（1色）カード。無色は対象外
          if (colors.length !== 1) return false;
          if (colorTokensInS > 0) { if (!S.has(colors[0])) return false; }
          else if (S.has("colorless")) return false; // 無色のみ選択なら単色は無し
          break;
        case "multi": // S の色を含む多色（2色以上）カード。無色は対象外
          if (colors.length < 2) return false;
          if (colorTokensInS > 0) { if (!colors.some((c) => S.has(c))) return false; }
          else if (S.has("colorless")) return false;
          break;
        case "exact": { // カードの文明集合が S と完全一致（無色も1トークンとして扱う）
          const tokens = civTokensOf(card);
          if (S.size > 0 && (tokens.length !== S.size || !tokens.every((c) => S.has(c)))) return false;
          break;
        }
        default: { // モード未指定: S のいずれかを含む（OR。無色は "colorless" 選択で一致）
          const tokens = civTokensOf(card);
          if (S.size > 0 && !tokens.some((c) => S.has(c))) return false;
        }
      }
    }
    if (filter.type && card.type !== filter.type) return false;
    if (filter.cost != null && card.cost !== filter.cost) return false;
    if (filter.race && !racesOf(card).includes(filter.race)) return false;
    if (nKeywords.length > 0) {
      const { ability } = searchTextOf(card);
      if (!nKeywords.every((k) => ability.includes(k))) return false;
    }
    if (nq) {
      const t = searchTextOf(card);
      if (!t.name.includes(nq) && !t.text.includes(nq)) return false;
    }
    return true;
  });
}
